import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouteErrorFallback } from './components/RouteErrorFallback';
import { LoadingScreen } from './components/ui/Spinner';
import { ProtectedRoute } from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import BooksPage from './pages/BooksPage';
import LoginPage from './pages/LoginPage';

const BookDetailPage = lazy(() => import('./pages/BookDetailPage'));
const AuthorsPage = lazy(() => import('./pages/AuthorsPage'));
const AuthorDetailPage = lazy(() => import('./pages/AuthorDetailPage'));
const ListsPage = lazy(() => import('./pages/ListsPage'));
const ListDetailPage = lazy(() => import('./pages/ListDetailPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BookReaderPage = lazy(() => import('./pages/BookReaderPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const VideoDetailPage = lazy(() => import('./pages/VideoDetailPage'));
const VideoGroupDetailPage = lazy(() => import('./pages/VideoGroupDetailPage'));
const AudioGroupsPage = lazy(() => import('./pages/AudioGroupsPage'));
const AudioGroupDetailPage = lazy(() => import('./pages/AudioGroupDetailPage'));
const SavedResponsesPage = lazy(() => import('./pages/SavedResponsesPage'));

function withSuspense(
  Component: React.LazyExoticComponent<() => React.ReactElement>,
  message?: string
) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen message={message} />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
}

function withErrorBoundary(element: React.ReactElement) {
  return <ErrorBoundary>{element}</ErrorBoundary>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: 'books/:id/read',
    element: (
      <ProtectedRoute>
        {withSuspense(BookReaderPage, 'Loading reader...')}
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, element: withErrorBoundary(<HomePage />) },
      { path: 'books', element: withErrorBoundary(<BooksPage />) },
      {
        path: 'books/:id',
        element: withSuspense(BookDetailPage, 'Loading book...'),
      },
      {
        path: 'authors',
        element: withSuspense(AuthorsPage, 'Loading authors...'),
      },
      {
        path: 'authors/:id',
        element: withSuspense(AuthorDetailPage, 'Loading author...'),
      },
      { path: 'videos', element: withSuspense(VideosPage, 'Loading videos...') },
      {
        path: 'videos/:id',
        element: withSuspense(VideoDetailPage, 'Loading video...'),
      },
      {
        path: 'video-groups/:id',
        element: withSuspense(VideoGroupDetailPage, 'Loading group...'),
      },
      { path: 'audio', element: withSuspense(AudioGroupsPage, 'Loading audio...') },
      {
        path: 'audio-groups/:id',
        element: withSuspense(AudioGroupDetailPage, 'Loading group...'),
      },
      { path: 'lists', element: withSuspense(ListsPage, 'Loading lists...') },
      {
        path: 'lists/:id',
        element: withSuspense(ListDetailPage, 'Loading list...'),
      },
      { path: 'chat', element: withSuspense(ChatPage, 'Loading chat...') },
      { path: 'saved', element: withSuspense(SavedResponsesPage, 'Loading saved responses...') },
      {
        path: 'upload',
        element: withSuspense(UploadPage, 'Loading uploader...'),
      },
      { path: 'admin', element: withSuspense(AdminPage, 'Loading admin...') },
      {
        path: 'settings',
        element: withSuspense(SettingsPage, 'Loading settings...'),
      },
    ],
  },
]);
